import { Fragment, useEffect, useState } from "react";
import axios from "axios";
import { initialData } from "@/lib/initialData";
import { stripHtml } from "@/lib/text";
import { whatsappLink } from "@/lib/whatsapp";
import { chosenFontsHref, fontVars, isDefaultFonts } from "@/lib/fonts";
import Header from "@/components/Header";
import HeroSection from "@/components/HeroSection";
import ScheduleSection from "@/components/ScheduleSection";
import CoachesSection from "@/components/CoachesSection";
import PricingSection from "@/components/PricingSection";
import InstagramSection from "@/components/InstagramSection";
import ContactSection from "@/components/ContactSection";
import Footer from "@/components/Footer";
import WhatsAppButton from "@/components/WhatsAppButton";
import { FaqSection, GallerySection, PillarsSection, TestimonialsSection, ValuesBand, faqList, galleryImages, testimonialsList } from "@/components/EkkoSections";

const API = `${process.env.REACT_APP_BACKEND_URL || ""}/api`;
const DAYS = ["segunda", "terca", "quarta", "quinta", "sexta", "sabado", "domingo"];
const DEFAULT_ORDER = ["hero", "values", "pillars", "schedule", "coaches", "testimonials", "pricing", "gallery", "faq", "instagram", "contact"];
const TIPO_LABELS = { aula_experimental: "Aula experimental", informacoes: "Pedido de informações", outro: "Outro" };

const hasSchedule = (s) => DAYS.some((d) => (s?.[d]?.aulas || []).length || (s?.[d]?.open_box || []).length);
const named = (items) => (Array.isArray(items) ? items.filter((i) => stripHtml(i?.nome)) : []);
const hasPricing = (p) =>
  ["mensalidades", "drop_in", "servicos_extra"].some((k) => named(p?.[k]).length > 0) ||
  ["joia", "seguro_anual", "curso_iniciacao"].some((k) => Number(p?.inscricao?.[k]) > 0);
const instagramPosts = (content) => (content.instagram_posts || []).filter((post) => post?.image);

// Cada secção: como se desenha, se tem conteúdo e a entrada no menu
function sectionCatalog(data, onContactSubmit) {
  const { content, schedule, prices, coaches } = data;
  return {
    hero: { render: () => <HeroSection content={content} priceFrom={prices?.preco_desde} /> },
    values: { render: () => <ValuesBand content={content} /> },
    pillars: { render: () => <PillarsSection content={content} />, nav: ["#pilares", "Pilares"] },
    schedule: { render: () => <ScheduleSection schedule={schedule} loading={false} content={content} />, has: () => hasSchedule(schedule), nav: ["#horarios", "Horários"] },
    coaches: { render: () => <CoachesSection coaches={coaches} loading={false} content={content} />, has: () => coaches.length > 0, nav: ["#equipa", "Equipa"] },
    pricing: { render: () => <PricingSection prices={prices} loading={false} content={content} />, has: () => hasPricing(prices), nav: ["#precos", "Preços"] },
    testimonials: { render: () => <TestimonialsSection content={content} />, has: () => testimonialsList(content).length > 0 },
    gallery: { render: () => <GallerySection content={content} />, has: () => galleryImages(content).length > 0, nav: ["#espaco", "Espaço"] },
    faq: { render: () => <FaqSection content={content} />, has: () => faqList(content).length > 0, nav: ["#perguntas", "Perguntas"] },
    instagram: { render: () => <InstagramSection content={content} />, has: () => instagramPosts(content).length > 0 },
    contact: { render: () => <ContactSection content={content} onSubmit={onContactSubmit} /> },
  };
}

function visibleKeys(content, catalog) {
  const saved = Array.isArray(content.layout_sections) ? content.layout_sections.filter((s) => catalog[s.key]) : [];
  const missing = DEFAULT_ORDER.filter((key) => !saved.some((s) => s.key === key)).map((key) => ({ key, visible: true }));
  return [...saved, ...missing].filter((s) => s.visible && (!catalog[s.key].has || catalog[s.key].has())).map((s) => s.key);
}

const FONT_LINK_ID = "ekko-chosen-fonts";

// Carrega a letra escolhida no painel quando não é a da marca (que já vem no index.html)
function useChosenFonts(content) {
  const href = isDefaultFonts(content) ? "" : chosenFontsHref(content);
  useEffect(() => {
    if (!href || document.querySelector(`link[href="${href}"]`)) return;
    const link = document.getElementById(FONT_LINK_ID) || Object.assign(document.createElement("link"), { id: FONT_LINK_ID, rel: "stylesheet" });
    link.href = href;
    document.head.appendChild(link);
  }, [href]);
}

export default function LandingPage() {
  const [data, setData] = useState(() => ({
    content: initialData?.content ?? {},
    schedule: initialData?.schedule ?? {},
    prices: initialData?.prices ?? {},
    coaches: initialData?.coaches ?? [],
  }));
  const [loading, setLoading] = useState(!initialData);
  useChosenFonts(data.content);

  useEffect(() => {
    if (initialData) return;
    Promise.all(["content", "schedule", "prices", "coaches"].map((name) => axios.get(`${API}/${name}`)))
      .then(([content, schedule, prices, coaches]) => setData({ content: content.data, schedule: schedule.data, prices: prices.data, coaches: coaches.data }))
      .catch((error) => console.error("Erro a carregar o conteúdo:", error))
      .finally(() => setLoading(false));
  }, []);


  // O formulário abre o WhatsApp do EKKO com a mensagem pronta; nada fica guardado no site
  const handleContactSubmit = async (form) => {
    const lines = [
      `Olá! ${TIPO_LABELS[form.tipo] || "Contacto"} pelo site do EKKO.`,
      `Nome: ${form.nome}`,
      `Telemóvel: ${form.telemovel}`,
      form.email && `Email: ${form.email}`,
      form.mensagem && `Mensagem: ${form.mensagem}`,
    ].filter(Boolean);
    window.open(whatsappLink(data.content, lines.join("\n")), "_blank", "noopener");
    return true;
  };

  if (loading) return <div className="site-ekko min-h-screen" aria-busy="true" />;

  const catalog = sectionCatalog(data, handleContactSubmit);
  const keys = visibleKeys(data.content, catalog);
  const navItems = keys.map((k) => catalog[k].nav).filter(Boolean).map(([href, label]) => ({ href, label }));
  const density = { compact: " density-compact", spacious: " density-spacious" }[data.content.theme_density] || "";

  return (
    <div
      className={`site-ekko min-h-screen relative${density}${data.content.theme_mode === "light" ? " theme-light" : ""}${data.content.titles_uppercase ? " titles-upper" : ""}`}
      style={fontVars(data.content)}
    >
      <Header content={data.content} navItems={navItems} />
      <main>
        {keys.map((k) => <Fragment key={k}>{catalog[k].render()}</Fragment>)}
      </main>
      <Footer content={data.content} navItems={navItems} />
      <WhatsAppButton content={data.content} />
    </div>
  );
}
